import { DialogType, SimCardSelection } from '../../../../common/enums.js';
import { ACTIVATE_EXISTING, SimOption } from '../../../../common/utils/simCardUtils.js';
import { openDialog } from '../../../../public/site/dialogStore.js';
import { useBundleAddonGroupSelectionCoCo } from '../../hooks/useBundleAddonGroupSelectionCoCo.js';
import { SimTypeCoCo } from '../../placeOrderTypesCoCo.js';
import { PlaceOrderSimCard } from '../../steps/products/PlaceOrderSimCard.js';
import { PlaceOrderSimCardCoCo } from '../../steps/products/PlaceOrderSimCardCoCo.js';
import {
  BundleAddonGroupRuntimeCharacteristic,
  isFixedMobileBroadband,
} from '../../utils/characteristicsFlagsUtilsCoCo.js';
import { hasSelectedCustomerOwnedCpe } from '../../utils/simCardSectionUtilsCoCo.js';
import type { ProductOfferingCartItems } from '../../../../common/types/shoppingBasketCoCo.js';
import type { SimCardConfiguration } from '../../../../common/types/subscription.js';
import type { SubscriptionConfigFormState } from '../../utils/subscriptionConfigurationUtilsCoCo.js';
import type { ProductConfigSchema } from '../useProductConfigSchemaCoCo.js';

export interface SimCardSectionProps {
  schema: ProductConfigSchema;
  basketItem: ProductOfferingCartItems;
  cartItemInstanceId: string;
  formState: SubscriptionConfigFormState;
  errors: Record<string, string>;
  simCardNumbers?: string[];
}

const getSelectedAddonName = (
  simType: SimTypeCoCo | typeof ACTIVATE_EXISTING
): BundleAddonGroupRuntimeCharacteristic =>
  simType === SimTypeCoCo.ESIM
    ? BundleAddonGroupRuntimeCharacteristic.ESIM
    : BundleAddonGroupRuntimeCharacteristic.PSIM;

export const SimCardSectionCoCo = ({
  schema,
  basketItem,
  cartItemInstanceId,
  formState,
  errors,
  simCardNumbers = [],
}: SimCardSectionProps) => {
  const { simType } = formState;

  const isActivateExisting = simType === ACTIVATE_EXISTING;
  const selectedAddonName = getSelectedAddonName(simType);

  useBundleAddonGroupSelectionCoCo({
    basketItem,
    isSelectedOffering: (name: string) => name === selectedAddonName,
  });

  if (!schema.needsEsim && !schema.needsPsim && !schema.needsActiveSim) {
    return null;
  }

  const simOptions: SimOption[] = [
    SimOption.ACTIVATE_EXISTING_PHYSICAL,
    ...(schema.needsEsim ? [SimOption.ORDER_NEW_ESIM] : []),
    ...(schema.needsPsim ? [SimOption.ORDER_NEW_PHYSICAL] : []),
  ];

  const simCardConfiguration: SimCardConfiguration = {
    simSelection: isActivateExisting ? SimCardSelection.ACTIVATE_EXISTING : SimCardSelection.ORDER_NEW,
    simType: isActivateExisting ? SimTypeCoCo.PHYSICAL : simType,
    simCardNumber: formState.simCardNumber,
  };

  const simCardProps = {
    cartItemInstanceId,
    onClickSimCardNumberHelp: () => openDialog({ type: DialogType.ORDER_SIM_CARD_NUMBER_HELP }),
    simCardConfiguration,
    simOptions,
    simCardNumbers,
    backendError: errors.simCardNumber,
    showESIM: schema.needsEsim,
    showPSIM: schema.needsPsim,
  };

  return (
    <div className="of-sim-card-section-coco">
      {isFixedMobileBroadband(basketItem) ? (
        <PlaceOrderSimCardCoCo
          {...simCardProps}
          showActiveSim={schema.needsActiveSim && hasSelectedCustomerOwnedCpe(basketItem)}
        />
      ) : (
        <PlaceOrderSimCard {...simCardProps} showActiveSim={schema.needsActiveSim} />
      )}
    </div>
  );
};
